import { AxiosError } from 'axios';
import { useQuery, UseQueryResult, UseQueryOptions } from '@tanstack/react-query';
import { couponQueryKeys } from '../../../constants/queryKeys/coupon';
import { CouponApiV2 } from '../../../lib/api/coupon/CouponApiV2';
import { setApplyCouponDiscount } from '../../../lib/utils/couponUtils';

type ProductLinkResponse = Awaited<ReturnType<typeof CouponApiV2.getCouponProductLinkList>>;

type ProductLinkOption = {
  couponId: number;
  discountType?: number;
  discountAmount?: number;
  maxDiscountPrice?: number;
  filterOption: Record<string, unknown>;
  pagination: { pageNum: number; pageSize: number };
};

type ProductLinkData = {
  list: Record<string, unknown>[];
  totalCnt: number;
  filterOption: Record<string, unknown>;
  pagination: { pageNum: number; pageSize: number; pages: number; totalCnt: number };
};

export const useCouponProductLinkQuery = (
  option: ProductLinkOption,
  options?: UseQueryOptions<ProductLinkResponse, AxiosError, ProductLinkData>,
): UseQueryResult<ProductLinkData, AxiosError> => {
  return useQuery(
    couponQueryKeys.couponProductLink(option.couponId, option),
    () => CouponApiV2.getCouponProductLinkList({
      couponId: option.couponId,
      filterOption: option.filterOption,
      pagination: option.pagination,
    }),
    {
      select: (res: ProductLinkResponse) => {
        const totalCnt = res.data.totalCnt ?? 0;
        let list = res.data.couponProductLinkList ?? [];
        if (option.discountType != null && option.discountAmount != null) {
          list = setApplyCouponDiscount(list, option.discountType, option.discountAmount);
        }
        return {
          list,
          totalCnt,
          filterOption: option.filterOption,
          pagination: {
            pageNum: option.pagination.pageNum,
            pageSize: option.pagination.pageSize,
            pages: Math.ceil(totalCnt / Math.max(1, option.pagination.pageSize)) || 1,
            totalCnt,
          },
        };
      },
      enabled: !!option.couponId,
      ...options,
    },
  );
};
